/**
 * Always mounts PostHogProvider so PostHog is available regardless of consent.
 * PostHog initialises with persistence:"memory" (no cookies/localStorage set until
 * analytics consent is granted). The PostHogConfigurator component (inside App)
 * upgrades/downgrades persistence at runtime when consent or auth state changes.
 *
 * Must be used inside ConsentProvider.
 */

import React from 'react';
import { PostHogProvider } from '@posthog/react';
import { registerLureoSuperProps } from '@/modules/analytics/superProperties';
import { setDeviceIdSource } from '@/infrastructure/clients/BackendClient';
import { runtimeEnv } from '@/lib/runtimeEnv';

const posthogHost = (runtimeEnv.VITE_PUBLIC_POSTHOG_HOST || (typeof process !== 'undefined' && process.env?.VITE_PUBLIC_POSTHOG_HOST)) as string | undefined;
const posthogKey = (runtimeEnv.VITE_PUBLIC_POSTHOG_KEY || (typeof process !== 'undefined' && process.env?.VITE_PUBLIC_POSTHOG_KEY)) as string | undefined;

// Capture kill switch. Non-prod builds set VITE_PUBLIC_POSTHOG_ENABLED=false so
// PostHog never initialises and dev/staging traffic stays out of the shared
// project. Absent/anything-but-"false" leaves capture enabled (production).
const posthogEnabledRaw = (runtimeEnv.VITE_PUBLIC_POSTHOG_ENABLED ?? (typeof process !== 'undefined' && process.env?.VITE_PUBLIC_POSTHOG_ENABLED)) as string | undefined;
const posthogEnabled = posthogEnabledRaw !== 'false';

const posthogOptions = {
  api_host: posthogHost,
  // Required once api_host points at the first-party proxy (e.lureo.app):
  // keeps toolbar/app links resolving to the real PostHog UI.
  ui_host: 'https://eu.posthog.com',
  defaults: '2025-11-30' as const,
  autocapture: false,
  // Start in cookieless memory mode; PostHogConfigurator upgrades to
  // "localStorage+cookie" once analytics consent is confirmed.
  persistence: 'memory' as const,
  // Register the client identity super property before the first event (the auto
  // $pageview) fires, so every event carries client=web.
  loaded: (posthog: unknown) => {
    registerLureoSuperProps(posthog as { register?: (p: Record<string, unknown>) => void });
    // The backend calls carry the device PostHog knows, so the backend's timing of each can be told apart per person
    const ph = posthog as { get_distinct_id?: () => string };
    setDeviceIdSource(() => ph.get_distinct_id?.());
  },
};

export function ConditionalPostHogProvider({ children }: { children: React.ReactNode }) {
  if (!posthogEnabled || !posthogKey || !posthogHost) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
      {children}
    </PostHogProvider>
  );
}
